/* AYAQ Cart Confirmation Modal Custom Element */

if (!customElements.get('ayaq-cart-modal')) {
  customElements.define('ayaq-cart-modal', class AyaqCartModal extends HTMLElement {
    constructor() {
      super();
      
      this.overlay = this.querySelector('.ayaq-cart-modal-overlay');
      this.container = this.querySelector('.ayaq-cart-modal-container');
      this.closeBtn = this.querySelector('.ayaq-cart-modal-close');
      
      this.closeBtn.addEventListener('click', this.close.bind(this));
      this.overlay.addEventListener('click', this.close.bind(this));
      this.overlay.addEventListener('touchmove', (e) => e.preventDefault(), { passive: false });
      
      this.recommendationsLoaded = false;
      
      // Close on any elements marked with data-action="close"
      for (const btn of this.querySelectorAll('[data-action="close"]')) {
        btn.addEventListener('click', this.close.bind(this));
      }
      
      this.escKeyHandler = (e) => {
        if (e.key === 'Escape' && this.container.classList.contains('is-open')) {
          this.close();
        }
      };

      // Swipe down to dismiss gesture on touch devices (mobile)
      if (this.container) {
        let startY = 0;
        let currentY = 0;
        let isDragging = false;
        let isAtTopWhenDragStarted = false;

        this.container.addEventListener('touchstart', (e) => {
          startY = e.touches[0].clientY;
          isAtTopWhenDragStarted = this.container.scrollTop <= 0;
          isDragging = true;
          this.container.style.transition = 'none';
        }, { passive: true });

        this.container.addEventListener('touchmove', (e) => {
          if (!isDragging || !isAtTopWhenDragStarted) return;
          currentY = e.touches[0].clientY;
          const diffY = currentY - startY;

          if (diffY > 0) {
            if (e.cancelable) e.preventDefault();
            this.container.style.transform = `translateY(${diffY}px)`;
          }
        }, { passive: false });

        const handleTouchEnd = () => {
          if (!isDragging) return;
          isDragging = false;
          this.container.style.transition = '';

          const diffY = currentY - startY;
          if (diffY > 100) {
            this.close();
          } else {
            this.container.style.transform = '';
          }
          currentY = 0;
          startY = 0;
        };

        this.container.addEventListener('touchend', handleTouchEnd);
        this.container.addEventListener('touchcancel', handleTouchEnd);
      }
    }

    connectedCallback() {
      document.addEventListener('keydown', this.escKeyHandler);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.escKeyHandler);
    }

    async openWithItem(item) {
      if (!item) return;

      const translations = window.ayaqCartModalTranslations || {};
      const currency = window.Shopify?.currency?.active || 'EUR';

      // 1. Image logic: first image of chosen color, fallback to item image
      let imageUrl = item.image;
      let colorOption = item.options_with_values?.find(opt => 
        opt.name.toLowerCase().includes('color') || opt.name.toLowerCase().includes('couleur')
      );
      let colorName = colorOption ? colorOption.value : '';
      
      if (window.ayaqColorImages && colorName && window.ayaqColorImages[colorName]) {
        imageUrl = window.ayaqColorImages[colorName];
      }

      const imgEl = this.querySelector('.ayaq-cart-modal-image');
      if (imgEl && imageUrl) {
        imgEl.src = imageUrl;
        imgEl.alt = item.title;
      }

      // 2. Title
      const titleEl = this.querySelector('.ayaq-cart-modal-info-title');
      if (titleEl) {
        titleEl.textContent = item.product_title || item.title;
      }

      // 3. Color Display
      const colorMetaEl = this.querySelector('[data-item-color]');
      if (colorMetaEl) {
        if (colorName) {
          colorMetaEl.style.display = 'block';
          colorMetaEl.textContent = `${translations.color || 'Couleur'} : ${colorName}`;
        } else {
          colorMetaEl.style.display = 'none';
        }
      }

      // 4. Size Display: using sizecorresp value from page
      const sizeMetaEl = this.querySelector('[data-item-size]');
      if (sizeMetaEl) {
        const sizeCorresp = document.getElementById('sizecorrespondance')?.textContent?.trim();
        let rawSizeOption = item.options_with_values?.find(opt => 
          opt.name.toLowerCase().includes('size') || opt.name.toLowerCase().includes('taille')
        );
        let rawSize = rawSizeOption ? rawSizeOption.value.trim() : '';
        if (!rawSize && item.variant_title && item.variant_title.includes('/')) {
          const parts = item.variant_title.split('/').map(p => p.trim());
          if (parts.length > 1) {
            rawSize = parts[parts.length - 1];
          }
        }
        
        let sizeVal = rawSize;
        if (sizeCorresp && sizeCorresp.toLowerCase() !== rawSize.toLowerCase()) {
          if (rawSize) {
            if (sizeCorresp.toLowerCase().startsWith(rawSize.toLowerCase())) {
              sizeVal = sizeCorresp;
            } else {
              sizeVal = `${rawSize} (${sizeCorresp})`;
            }
          } else {
            sizeVal = sizeCorresp;
          }
        }

        if (sizeVal) {
          sizeMetaEl.style.display = 'block';
          sizeMetaEl.textContent = `${translations.size || 'Taille'} : ${sizeVal}`;
        } else {
          sizeMetaEl.style.display = 'none';
        }
      }

      // 5. Price
      const priceEl = this.querySelector('.ayaq-cart-modal-info-price');
      if (priceEl && item.final_price) {
        priceEl.textContent = this.#formatMoney(item.final_price / 100, currency);
      }

      // 6. Fetch Cart total information dynamically
      fetch('/cart.js')
        .then(res => res.json())
        .then(cart => {
          const count = cart.item_count;
          let countText = count > 1 
            ? (translations.cart_count_other || '{{ count }} articles dans votre panier') 
            : (translations.cart_count_one || '1 article dans votre panier');
          
          countText = countText.replace('{{ count }}', count).replace('{count}', count).replace('%{count}', count);
          
          const countEl = this.querySelector('.ayaq-cart-modal-summary-count');
          if (countEl) countEl.textContent = countText;

          const totalEl = this.querySelector('.ayaq-cart-modal-summary-total');
          if (totalEl) {
            totalEl.textContent = `${translations.total || 'Total'} : ${this.#formatMoney(cart.total_price / 100, cart.currency)}`;
          }
        })
        .catch(err => console.error('Error fetching cart:', err));

      // 7. Update shipping fees & promotion messages dynamically
      this.#updateShippingMessages();

      // 8. Load Complementary recommendations synchronously/async before opening modal
      await this.#loadRecommendations(item);

      // 9. Open modal and drawer transitions (now recommendations are already rendered!)
      this.overlay.classList.add('is-visible');
      this.container.classList.add('is-open');
      document.documentElement.classList.add('ayaq-cart-modal-open');
      document.body.classList.add('ayaq-cart-modal-open');
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';

      // Lets the other overlays (search panel, drawers) step aside so they never
      // cover the confirmation.
      document.dispatchEvent(new CustomEvent('ayaq:cart-modal-open'));
    }

    #updateShippingMessages() {
      const drawerSpecialMsg = document.querySelector('#CartDrawer .special_message');
      const modalSpecialMsgContainer = this.querySelector('.ayaq-cart-modal-special-message');
      if (modalSpecialMsgContainer) {
        if (drawerSpecialMsg) {
          modalSpecialMsgContainer.innerHTML = drawerSpecialMsg.outerHTML;
          modalSpecialMsgContainer.style.display = '';
        }
      }

      const drawerShippingPromo = document.querySelector('#CartDrawer .shipping-promo');
      const modalShippingPromoContainer = this.querySelector('.ayaq-cart-modal-shipping-promo');
      if (modalShippingPromoContainer) {
        if (drawerShippingPromo) {
          modalShippingPromoContainer.innerHTML = drawerShippingPromo.outerHTML;
          modalShippingPromoContainer.style.display = '';
        } else {
          modalShippingPromoContainer.innerHTML = '';
          modalShippingPromoContainer.style.display = 'none';
        }
      }
    }

    close() {
      this.container.style.transform = '';
      this.container.classList.remove('is-open');
      this.overlay.classList.remove('is-visible');
      document.documentElement.classList.remove('ayaq-cart-modal-open');
      document.body.classList.remove('ayaq-cart-modal-open');
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }

    #loadRecommendations(item) {
      const compContainer = this.querySelector('.ayaq-cart-modal-complementary');
      const compGrid = this.querySelector('.ayaq-cart-modal-complementary-grid');

      if (!compContainer || !compGrid || !item) return Promise.resolve();

      this.recommendationsLoaded = true;
      compGrid.innerHTML = '';

      // 1. Check if complementary-metafield exists in current DOM
      const existingCards = document.querySelectorAll('.complementary-metafield .grid__item, .complementary-products .grid__item');
      if (existingCards && existingCards.length > 0 && (window.location.pathname.includes(item.handle) || !item.handle)) {
        this.#renderComplementaryCards(existingCards);
        return Promise.resolve();
      }

      // 2. Fetch main-product section for item.handle to get exact metafield complementary products
      const handle = item.handle || (item.url ? item.url.split('/products/')[1]?.split('?')[0] : '');
      const productId = item.product_id || item.id;

      if (handle) {
        return fetch(`/products/${handle}?section_id=main-product`)
          .then(res => res.text())
          .then(html => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const cards = doc.querySelectorAll('.complementary-metafield .grid__item, .complementary-products .grid__item');
            
            if (cards && cards.length > 0) {
              this.#renderComplementaryCards(cards);
            } else {
              return this.#fetchRecommendationsJson(productId);
            }
          })
          .catch(() => {
            return this.#fetchRecommendationsJson(productId);
          });
      } else if (productId) {
        return this.#fetchRecommendationsJson(productId);
      }
      return Promise.resolve();
    }

    #renderComplementaryCards(cards) {
      const compContainer = this.querySelector('.ayaq-cart-modal-complementary');
      const compGrid = this.querySelector('.ayaq-cart-modal-complementary-grid');
      if (!compContainer || !compGrid) return;

      compGrid.innerHTML = '';
      const seenTitles = new Set();
      let count = 0;

      for (const origCard of cards) {
        if (count >= 4) break;

        const titleEl = origCard.querySelector('.card__heading a, .card__heading');
        const title = titleEl ? titleEl.textContent.trim() : '';
        if (!title || seenTitles.has(title)) continue;
        seenTitles.add(title);

        const url = titleEl && titleEl.tagName === 'A' ? titleEl.getAttribute('href') : (origCard.querySelector('a') ? origCard.querySelector('a').getAttribute('href') : '#');
        const imgEl = origCard.querySelector('.card__media img, .media img');
        const imgSrc = imgEl ? (imgEl.getAttribute('src') || imgEl.src) : '';
        const priceEl = origCard.querySelector('.price');
        const priceHtml = priceEl ? priceEl.innerHTML : '';

        // Extract activity icons HTML
        const activitiesEl = origCard.querySelector('.product-card__activities-container, .product-card__activities');
        let activitiesHtml = '';
        if (activitiesEl) {
          if (activitiesEl.classList.contains('product-card__activities-container')) {
            activitiesHtml = activitiesEl.outerHTML;
          } else {
            activitiesHtml = `<div class="product-card__activities-container">${activitiesEl.outerHTML}</div>`;
          }
        }

        const card = document.createElement('a');
        card.className = 'ayaq-cart-modal-card';
        card.href = url;

        card.innerHTML = `
          <div class="ayaq-cart-modal-card-image-wrapper">
            <img class="ayaq-cart-modal-card-image" src="${imgSrc}" alt="${title}" loading="lazy" width="150" height="200">
            ${activitiesHtml}
          </div>
          <p class="ayaq-cart-modal-card-title">${title}</p>
          <div class="ayaq-cart-modal-card-price price">${priceHtml}</div>
        `;

        compGrid.appendChild(card);
        count++;
      }

      if (count > 0) {
        compContainer.style.display = 'block';
      } else {
        compContainer.style.display = 'none';
      }
    }

    #fetchRecommendationsJson(productId) {
      const compContainer = this.querySelector('.ayaq-cart-modal-complementary');
      const compGrid = this.querySelector('.ayaq-cart-modal-complementary-grid');
      if (!compContainer || !compGrid || !productId) return;

      fetch(`/recommendations/products.json?product_id=${productId}&limit=4&intent=complementary`)
        .then(res => res.json())
        .then(data => {
          let products = data.products || [];
          if (products.length === 0) {
            return fetch(`/recommendations/products.json?product_id=${productId}&limit=4&intent=related`)
              .then(res => res.json())
              .then(relData => relData.products || []);
          }
          return products;
        })
        .then(products => {
          if (products && products.length > 0) {
            compContainer.style.display = 'block';
            const seenIds = new Set();
            const uniqueProducts = products.filter(p => {
              if (!p || !p.id || seenIds.has(p.id)) return false;
              if (!p.variants?.some(variant => variant.featured_image)) return false;
              seenIds.add(p.id);
              return true;
            }).slice(0, 4);

            for (const prod of uniqueProducts) {
              const eligible = prod.variants.filter(variant => variant.featured_image);
              const variant = eligible.find(item => item.available) || eligible[0];
              const title = prod.title || '';
              const productUrl = new window.URL(prod.url || `/products/${prod.handle}`, window.location.origin);
              productUrl.searchParams.set('variant', variant.id);
              const url = productUrl.pathname + productUrl.search;
              const imgSrc = variant.featured_image.src;
              const currency = window.Shopify?.currency?.active || 'EUR';
              const priceVal = (variant.price / 100);
              const priceFormatted = this.#formatMoney(priceVal, currency);

              const card = document.createElement('a');
              card.className = 'ayaq-cart-modal-card';
              card.href = url;

              card.innerHTML = `
                <div class="ayaq-cart-modal-card-image-wrapper">
                  <img class="ayaq-cart-modal-card-image" src="${imgSrc}" alt="${title}" loading="lazy" width="150" height="200">
                </div>
                <p class="ayaq-cart-modal-card-title">${title}</p>
                <div class="ayaq-cart-modal-card-price price">${priceFormatted}</div>
              `;

              compGrid.appendChild(card);
            }
          } else {
            compContainer.style.display = 'none';
          }
        })
        .catch(() => {
          compContainer.style.display = 'none';
        });
    }

    #formatMoney(value, currency) {
      const activeCurrency = currency || window.Shopify?.currency?.active || 'EUR';
      const roundedVal = Math.round(value);

      const symbolMap = {
        EUR: '€',
        USD: '$',
        GBP: '£',
        JPY: '¥',
        CAD: 'CA$',
        AUD: 'AU$',
        CHF: 'CHF'
      };

      try {
        const formattedNum = new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(roundedVal);

        if (activeCurrency === 'EUR') {
          const eurNum = new Intl.NumberFormat('fr-FR', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(roundedVal);
          return `${eurNum} €`;
        }

        if (activeCurrency === 'JPY') {
          return `¥${formattedNum}`;
        }

        if (activeCurrency === 'USD') {
          return `$${formattedNum}`;
        }

        if (activeCurrency === 'GBP') {
          return `£${formattedNum}`;
        }

        const symbol = symbolMap[activeCurrency] || activeCurrency;
        return `${symbol}${formattedNum}`;
      } catch (e) {
        return `${activeCurrency} ${roundedVal}`;
      }
    }
  });
}
